import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { Switch } from "@shared/ui/switch";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllSchedules, useUpdateScheduleStatus } from "../../hooks";
import SchedulesDashboard from "./dashboard/ScheduleDashboard";
import { SchedulesFilterPanel } from "./filter/filter";

const getTypeColor = (type: string) => {
  switch (type) {
    case "Preventive Maintenance":
      return "bg-blue-100 text-blue-800";
    case "Inspection":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "Active":
      return "bg-green-100 text-green-800";
    case "Inactive":
      return "bg-gray-100 text-gray-800";
    case "Draft":
      return "bg-yellow-100 text-yellow-800";
    case "Overdue":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const Schedules: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<any>({
    status: "all",
    frequency_type: "all",
    page: 1,
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Use the hook to get current data with filters
  const { data: schedulesData } = useGetAllSchedules(
    currentPage,
    PAGE_SIZE,
    searchTerm,
    filters.status !== "all" ? filters.status : undefined,
    filters.frequency_type !== "all" ? filters.frequency_type : undefined
  );

  const updateScheduleStatus = useUpdateScheduleStatus(currentPage, PAGE_SIZE);

  const columns: TableColumn<any>[] = [
    {
      key: "scheduleNo",
      header: "Schedule ID",
      type: "text",
      headerTooltip:
        "Unique identifier for each preventive maintenance or inspection schedule.",
    },
    {
      key: "scheduleName",
      header: "Schedule Name",
      type: "text",
      headerTooltip:
        "Descriptive title of the schedule to help identify its purpose and scope.",
    },
    {
      key: "scheduleTypeDisplay",
      header: "Type",
      headerTooltip:
        "Indicates whether the schedule is for Preventive Maintenance (PM) or Inspection (INSP).",
      type: "badge",
      badgeConfig: {
        getClassName: (value: string) => getTypeColor(value),
      },
    },
    {
      key: "frequencyTypeDisplay",
      header: "Frequency",
      type: "text",
      headerTooltip:
        "The recurrence interval of the schedule (e.g., Monthly, Quarterly, Annually, or usage-based like Every 500 Hrs).",
    },
    {
      key: "runCount",
      header: "Runs",
      type: "custom",
      headerTooltip:
        "Total number of times this schedule has been executed since creation.",
      render: (value: number) => (
        <span className="ml-6 font-medium">{value || 0}</span>
      ),
    },
    {
      key: "totalEntities",
      header: "Entities",
      headerTooltip: "Number of entities covered under this schedule.",
      render: (value: number) => (
        <span className="ml-6 font-medium">{value || 0}</span>
      ),
    },
    {
      key: "nextExecutionDate",
      header: "Next Due Date",
      type: "text",
      headerTooltip:
        "The planned date for the next scheduled task to be carried out.",
    },
    {
      key: "lastExecutionDate",
      header: "Last Completed",
      type: "text",
      headerTooltip:
        "The date when the last run of this schedule was successfully completed.",
    },
    {
      key: "progressPercentage",
      header: "Progress",
      type: "text",
      headerTooltip:
        "Shows the number of tasks completed versus total assigned tasks in the current cycle.",
    },
    {
      key: "isActive",
      header: "Activate/Deactivate",
      headerTooltip: "Toggle to activate or deactivate the schedule.",
      render: (value, row) => (
        <div className="flex justify-center gap-2">
          <Switch
            checked={row.isActive}
            onCheckedChange={(checked) =>
              updateScheduleStatus.mutateAsync({
                id: row.id,
                isActive: checked,
              })
            }
          />
        </div>
      ),
    },
  ];

  const totalItems = schedulesData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalPages, totalItems, startIndex]
  );

  const handleExport = () => {};

  const handleViewDetails = (scheduleId: string) => {
    navigate(`/ax/service-order/schedule/view/${scheduleId}`);
  };

  const handleEditSchedule = (scheduleId: string) => {
    navigate(`/ax/service-order/schedule/edit/${scheduleId}`);
  };

  const actions: TableAction<any>[] = [
    {
      label: "View Details",
      onClick: (item) => {
        handleViewDetails(item.id);
      },
      icon: "Eye",
      variant: "outline",
    },
    {
      label: "Edit Schedule",
      onClick: (item) => {
        handleEditSchedule(item.id);
      },
      icon: "Pen",
      variant: "outline",
    },
  ];

  // Filter handlers
  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    const defaultFilters: any = {
      status: "all",
      frequency_type: "all",
      page: 1,
    };
    setFilters(defaultFilters);
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen bg-gray-50 bg-white">
      <div className="max-w-[1400px] mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <PageHeader
            title=" Preventive Maintenance & Inspections"
            subtitle="Manage and track all scheduled preventive maintenance tasks and
              inspections"
          />

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => navigate("/ax/service-order/schedule/create")}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Schedule
            </Button>
          </div>
        </div>

        {/* Dashboard Metrics */}
        <SchedulesDashboard />

        {/* Schedules Table with Filter Panel */}
        <div>
          <GenericTable
            data={schedulesData?.results || []}
            columns={columns}
            rowKey="id"
            searchPlaceholder="Search schedules by name, ID..."
            searchParamName="query"
            searchDebounceMs={300}
            searchValue={searchTerm}
            onSearch={handleSearch}
            pagination={pagination}
            onPageChange={handlePageChange}
            actions={actions}
            actionsType="dropdown"
            onExport={handleExport}
            emptyMessage="No schedules found matching your criteria"
            filterPanel={
              <SchedulesFilterPanel
                onFiltersChange={handleFiltersChange}
                onClearFilters={handleClearFilters}
                onApplyFilters={handleFiltersChange}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

export default Schedules;
